import { useState, useEffect } from "react";
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Paper, Grid, Autocomplete, CircularProgress, TextField, } from "@mui/material";
import LessorFilter from "./LessorFilter";
import Header from "./Header";
import { EndDateFilter, StartDateFilter } from "./DateFilter";
import { getAccountsReceivableReportApi } from "../apiService";
import { globalGetService, globalPostService } from "../../../utils/globalApiServices";
import { checkApiStatus } from "../../../utils";
import { ExportMenu, gaTelesisInstance, PageLoader } from "../../../shared_elements";
import { isValidYearRange } from "../../../utils/helper";
import AssetFilter from "./AssetFilter";
import { backendDateFormat } from "../../../constants";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { trackActivity } from "../../../utils/mixpanel";

const leaseStatusOptions = ["Active", "Closed", "Terminated", "On-hold"];

const AccountsReceivableReport = ({ pltConstants, dropDownLoader }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false)
  const [reportData, setReportData] = useState({});
  const [error, setError] = useState({});
  const [showMore, setShowMore] = useState(false);
  const onClickMultiFilter = (data, type, key) => { if (type === "all") { onFieldChange(key, data); } else { onFieldChange(key, []); } };
  const onFieldChange = (key, value) => {
    setReportData((prev) => {
      const updatedData = { ...prev, [key]: value };
      if (key === "start_date" && !value) { updatedData.end_date = null; }
      return updatedData;
    });
  };  
  const onFocusChange = (keyParam) => { setError(prev => ({ ...prev, [keyParam]: '' })) }
  const onExportReport = (extention,) => {
    const validationInputs = {
      start_date: !reportData?.start_date
        ? (gaTelesisInstance ? "" : "Please select Start Date")
        : (!isValidYearRange(reportData.start_date)
          ? "Year must be between 1900 and 2100."
          : ""),
      end_date: !reportData?.end_date
        ? (gaTelesisInstance ? "" : "Please select End Date")
        : (reportData?.start_date && moment(reportData.end_date).isBefore(moment(reportData.start_date))
          ? "End date must be after Start Date"
          : !isValidYearRange(reportData.end_date)
            ? "Year must be between 1900 and 2100."
            : ""),
    };
    if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; })) {
      const getIdArray = (arr) => arr?.length ? arr.map((i) => i.id) : undefined;
      const lesseeIds = getIdArray(reportData?.lessee);
      const payload = {
        statement_type: 'account',
        file_type: extention, start_date: reportData?.start_date, end_date: reportData?.end_date ? reportData?.end_date : gaTelesisInstance ? moment().format(backendDateFormat) : null,
        ...(reportData?.lessor_name_id?.length && { lessor_name_id: reportData.lessor_name_id.map(i => i.id) }),
        ...(lesseeIds && { lessee: lesseeIds }),
        ...(reportData?.asset_type_id?.length && { asset_serial_number: reportData.asset_type_id.map(i => ({ id: parseInt(i.id), asset_type: i.asset_type })) }),
        ...(reportData?.aircraft_types?.length && { aircraft_type: reportData.aircraft_types.map(i => i.id) }),
        ...(reportData?.engine_types?.length && { engine_type: reportData.engine_types.map(i => i.id) }),
        ...(reportData?.apu_type?.length && { apu_type: reportData.apu_type.map(i => i.id) }),
        ...(reportData?.lease_status?.length && { lease_status: reportData.lease_status }),
        ...(reportData?.invoice_type?.length && { invoice_type: reportData.invoice_type.map(i => i.value) }),
        ...(reportData?.country?.length && { country: reportData.country.map(i => i.name) }),
      }
      setLoading(true)
      globalPostService(getAccountsReceivableReportApi(),payload)
        .then(response => {
          setLoading(false)
          if (checkApiStatus(response)) {
            setReportData({})
            enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { vertical: "top", horizontal: "right" }, })
            trackActivity('Item Export', { page_title: `Accounts Receivable Report`, success_msg: response?.data?.message, payload: payload, response: response?.data })
          } else {
            enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" }, })
            trackActivity('Item Export', { page_title: `Accounts Receivable Report`, error_msg: response?.data?.message, payload: payload, response: response?.data })
          }
        });
    } else {
      setError(validationInputs)
      trackActivity('Item Export', { page_title: `Accounts Receivable Report`, error_msg: validationInputs })
    }
  };
  return (
    <Paper square>
      <div className='custom-rpt-card'>
        <div className='rpt-card-header'>
          <Grid container spacing={1} className='rpt-header-grid'>
            <Grid size={{ md: 10, xs: 10 }}>
              <Header title="Accounts Receivables Report" description="Open Invoices Report; AR Aging." />
            </Grid>
            <Grid size={{ md: 2, xs: 2 }} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
              <ExportMenu
                exportReportFn={(file) => onExportReport(file.extension)}
                files={[{ title: 'EXCEL', extension: 'xlsx' }, { title: 'CSV', extension: 'csv' }]}
              />
            </Grid>
          </Grid>
        </div>
        <Grid sx={{ padding: 2 }} container spacing={1} >
          <Grid size={{ md: 6, xs: 12 }} >
           <StartDateFilter reportData={reportData} onFieldChange={onFieldChange} onFocusChange={onFocusChange} error={error} isFutureDisabled={true} isRequired={!gaTelesisInstance}/>
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <EndDateFilter reportData={reportData} onFieldChange={onFieldChange} onFocusChange={onFocusChange} error={error} disabled={!reportData?.start_date} isRequired={!gaTelesisInstance} disableFuture={true}/>
          </Grid>
          {gaTelesisInstance &&
            <Grid size={{ md: 6, xs: 12 }}>
              <p className='lessor-select'><span onClick={() => onClickMultiFilter(leaseStatusOptions, "all", "lease_status")}>Select All Lease Status</span>{reportData?.lease_status?.length ? <span style={{ marginLeft: '8px' }} onClick={() => onClickMultiFilter(leaseStatusOptions, "remove", "lease_status")} >Remove All Lease Status</span> : null}</p>
              <Autocomplete
                disablePortal
                options={leaseStatusOptions}
                multiple
                noOptionsText="No options"
                value={reportData?.lease_status || []}
                onChange={(e, value) => onFieldChange("lease_status", value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lease Status"
                    margin='normal'
                    size='small'
                    placeholder="Select Lease Status(s)"
                    slotProps={{
                      inputLabel: { shrink: true, },
                    }}
                  />
                )}
              />
            </Grid>}
          <Grid size={{ md: 6, xs: 12 }}>
            <LessorFilter pltConstants={pltConstants} dropDownLoader={dropDownLoader} reportData={reportData} onFieldChange={onFieldChange} />
          </Grid>
          {gaTelesisInstance && !showMore && (
          <Grid size={{ md: 12, xs: 12 }}>
            <p className='lessor-select'><span className='show-more-toggle' onClick={() => setShowMore(true)}>Show more <ChevronRightIcon sx={{ fontSize: 15 }} /></span></p>
          </Grid>
          )}
          {showMore && (<>
          {gaTelesisInstance &&(<>
          <Grid size={{ md: 6, xs: 12 }}>
            <p className='lessor-select'><span onClick={() => onClickMultiFilter(pltConstants?.lessee, "all", "lessee")}>Select All Lessee</span>{reportData?.lessee?.length ? <span style={{ marginLeft: '8px' }} onClick={() => onClickMultiFilter(pltConstants?.lessee, "remove", "lessee")} >Remove All Lessee</span> : null}</p>
            <Autocomplete
              disablePortal
              options={pltConstants?.lessee || []}
              multiple
              noOptionsText={dropDownLoader?.lessee ? "Loading..." : "No options"}
              value={reportData?.lessee || []}
              getOptionLabel={(option) => option.name || ""}
              onChange={(e, value) => onFieldChange("lessee", value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lessee"
                  margin='normal'
                  size='small'
                  placeholder="Select Lessee(s)"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {dropDownLoader?.lessor && (
                            <CircularProgress color="inherit" size={18} alignItems="center" />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                    inputLabel: { shrink: true, },
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <AssetFilter isRequired={false} pltConstants={pltConstants} dropDownLoader={dropDownLoader} reportData={reportData} onFieldChange={onFieldChange} onFocusChange={onFocusChange} />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <p className='lessor-select'><span onClick={() => onClickMultiFilter(pltConstants?.aircraft_types, "all", "aircraft_types")}>Select All Aircraft Types</span>{reportData?.aircraft_types?.length ? <span style={{ marginLeft: '8px' }} onClick={() => onClickMultiFilter(pltConstants?.aircraft_types, "remove", "aircraft_types")} >Remove All Aircraft Types</span> : null}</p>
            <Autocomplete
              disablePortal
              options={pltConstants?.aircraft_types || []}
              multiple
              noOptionsText={dropDownLoader?.aircraft_types ? "Loading..." : "No options"}
              value={reportData?.aircraft_types || []}
              getOptionLabel={(option) => option.name || ""}
              onChange={(e, value) => onFieldChange("aircraft_types", value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Aircraft Type"
                  margin='normal'
                  size='small'
                  placeholder="Select Aircraft Type(s)"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {dropDownLoader?.aircraft_types && (
                            <CircularProgress color="inherit" size={18} alignItems="center" />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                    inputLabel: { shrink: true, },
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <p className='lessor-select'><span onClick={() => onClickMultiFilter(pltConstants?.engine_types, "all", "engine_types")}>Select All Engine Types</span>{reportData?.engine_types?.length ? <span style={{ marginLeft: '8px' }} onClick={() => onClickMultiFilter(pltConstants?.engine_types, "remove", "engine_types")} >Remove All Engine Types</span> : null}</p>
            <Autocomplete
              disablePortal
              options={pltConstants?.engine_types || []}
              multiple
              noOptionsText={dropDownLoader?.engine_types ? "Loading..." : "No options"}
              value={reportData?.engine_types || []}
              getOptionLabel={(option) => option.name || ""}
              onChange={(e, value) => onFieldChange("engine_types", value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Engine Type"
                  margin='normal'
                  size='small'
                  placeholder="Select Engine Type(s)"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {dropDownLoader?.engine_types && (
                            <CircularProgress color="inherit" size={18} alignItems="center" />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                    inputLabel: { shrink: true, },
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <p className='lessor-select'><span onClick={() => onClickMultiFilter(pltConstants?.apu_types, "all", "apu_type")}>Select All APU Types</span>{reportData?.apu_type?.length ? <span style={{ marginLeft: '8px' }} onClick={() => onClickMultiFilter(pltConstants?.apu_types, "remove", "apu_type")} >Remove All APU Types</span> : null}</p>
            <Autocomplete
              disablePortal
              options={pltConstants?.apu_types || []}
              multiple
              noOptionsText={dropDownLoader?.apu_types ? "Loading..." : "No options"}
              value={reportData?.apu_type || []}
              getOptionLabel={(option) => option.name || ""}
              onChange={(e, value) => onFieldChange("apu_type", value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="APU Type"
                  margin='normal'
                  size='small'
                  placeholder="Select APU Type(s)"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {dropDownLoader?.apu_types && (
                            <CircularProgress color="inherit" size={18} alignItems="center" />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                    inputLabel: { shrink: true, },
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <p className='lessor-select'><span onClick={() => onClickMultiFilter(pltConstants?.invoice_type, "all", "invoice_type")}>Select All Invoice Types</span>{reportData?.invoice_type?.length ? <span style={{ marginLeft: '8px' }} onClick={() => onClickMultiFilter(pltConstants?.invoice_type, "remove", "invoice_type")} >Remove All Invoice Types</span> : null}</p>
            <Autocomplete
              disablePortal
              options={pltConstants?.invoice_type || []}
              multiple
              noOptionsText={dropDownLoader?.invoice_type ? "Loading..." : "No options"}
              value={reportData?.invoice_type || []}
              getOptionLabel={(option) => option.label || ""}
              onChange={(e, value) => onFieldChange("invoice_type", value)}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Invoice Type"
                  margin='normal'
                  size='small'
                  placeholder="Select Invoice Type(s)"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {dropDownLoader?.invoice_type && (
                            <CircularProgress color="inherit" size={18} alignItems="center" />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                    inputLabel: { shrink: true, },
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <p className='lessor-select'><span onClick={() => onClickMultiFilter(pltConstants?.countries, "all",'country')}>Select All Countries</span>{reportData?.country?.length ? <span style={{ marginLeft: '8px' }} onClick={() => onClickMultiFilter(pltConstants?.countries, "remove", 'country')} >Remove All Countries</span> : null}</p>
            <Autocomplete
              disablePortal
              options={pltConstants?.countries || []}
              multiple
              noOptionsText={dropDownLoader?.country ? "Loading..." : "No options"}
              value={reportData?.country || []}
              getOptionLabel={(option) => option.name || ""}
              onChange={(e, value) => onFieldChange("country", value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country"
                  margin='normal'
                  size='small'
                  placeholder="Select Country(s)"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {dropDownLoader?.country && (
                            <CircularProgress color="inherit" size={18} alignItems="center" />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                    inputLabel: { shrink: true, },
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ md: 12, xs: 12 }}>
            <p className='lessor-select'><span className='show-more-toggle' onClick={() => setShowMore(false)}><ChevronLeftIcon sx={{ fontSize: 15 }} /> Show less</span></p>
          </Grid></>)}
          </>)}
        </Grid>
      </div>
      {isLoading ? <PageLoader /> : null}
    </Paper>
  )
}

export default AccountsReceivableReport
